import type { BillingMode, PlanDefinition, PlanId, Subscription } from '../../api';

/**
 * Catalogue des plans (affichage et estimation). Les prix sont en EUR par unité et par mois ;
 * le serveur reste l'autorité (montants facturés, quotas appliqués).
 */
export interface PlanSpec {
  id: PlanId;
  /** Prix mensuel par technicien ; null = sur devis. */
  perTechnician: number | null;
  /** Prix mensuel par appareil ; null = sur devis. */
  perDevice: number | null;
  /** Quotas ; null = illimité. */
  technicians: number | null;
  devices: number | null;
  aiCredits: number | null;
  auditRetentionDays: number;
  sso: boolean;
  recordings: boolean;
  whiteLabel: boolean;
  support: 'community' | 'email' | 'priority' | 'dedicated';
}

/** Plans dont le nombre de techniciens se choisit à la souscription (tarif par technicien et par mois). */
export const SEAT_PLANS: PlanId[] = ['pro', 'team'];
export const MIN_SEATS = 1;
export const MAX_SEATS = 25;
export const isSeatPlan = (id: PlanId) => SEAT_PLANS.includes(id);

export const PLAN_IDS: PlanId[] = ['free', 'pro', 'team', 'enterprise'];

export const PLANS: Record<PlanId, PlanSpec> = {
  free: { id: 'free', perTechnician: 0, perDevice: 0, technicians: 1, devices: 5, aiCredits: 50, auditRetentionDays: 30, sso: false, recordings: false, whiteLabel: false, support: 'community' },
  pro: { id: 'pro', perTechnician: 9, perDevice: 1.5, technicians: 3, devices: 50, aiCredits: 500, auditRetentionDays: 90, sso: false, recordings: false, whiteLabel: false, support: 'email' },
  team: { id: 'team', perTechnician: 19, perDevice: 1.2, technicians: 10, devices: null, aiCredits: 2000, auditRetentionDays: 365, sso: true, recordings: true, whiteLabel: false, support: 'priority' },
  enterprise: { id: 'enterprise', perTechnician: null, perDevice: null, technicians: null, devices: null, aiCredits: 10000, auditRetentionDays: 730, sso: true, recordings: true, whiteLabel: true, support: 'dedicated' },
};

/** Seuils à partir desquels un quota serveur est présenté comme illimité (plan Entreprise : 10 000 / 100 000). */
export const UNLIMITED_TECHNICIANS = 10_000;
export const UNLIMITED_DEVICES = 100_000;

/**
 * Applique le catalogue serveur (autorité sur quotas et SSO) aux fiches d'affichage : ne garde que les plans actifs
 * connus de la console, dans l'ordre du serveur. Sans catalogue (chargement, erreur), les valeurs locales servent de repli.
 */
export function mergeServerPlans(base: Record<PlanId, PlanSpec>, defs: PlanDefinition[] | undefined): { ids: PlanId[]; specs: Record<PlanId, PlanSpec> } {
  if (!defs || defs.length === 0) return { ids: PLAN_IDS, specs: base };
  const specs = { ...base };
  const ids: PlanId[] = [];
  for (const d of [...defs].sort((a, b) => a.sortOrder - b.sortOrder)) {
    const id = d.id as PlanId;
    if (!PLAN_IDS.includes(id) || !d.active) continue;
    specs[id] = {
      ...base[id],
      technicians: d.quotas.technicians >= UNLIMITED_TECHNICIANS ? null : d.quotas.technicians,
      devices: d.quotas.devices >= UNLIMITED_DEVICES ? null : d.quotas.devices,
      sso: d.features.includes('sso'),
    };
    ids.push(id);
  }
  return ids.length > 0 ? { ids, specs } : { ids: PLAN_IDS, specs: base };
}

/** Taux indicatif EUR -> DA pour l'affichage ; la facturation SATIM est réglée en DA au taux du contrat. */
export const EUR_TO_DZD = 145;

/** Prix d'un crédit IA en EUR (aligné sur le serveur). */
export const CREDIT_UNIT_EUR = 0.02;
export const CREDIT_PACKS = [500, 2000, 10000] as const;

/** Estimation mensuelle selon le mode de facturation ; null = sur devis (Entreprise). */
export function estimateMonthly(planId: PlanId, mode: BillingMode, usage: { technicians: number; devices: number }, specs: Record<PlanId, PlanSpec> = PLANS): number | null {
  const plan = specs[planId];
  const unit = mode === 'per_technician' ? plan.perTechnician : plan.perDevice;
  if (unit === null) return null;
  const qty = mode === 'per_technician' ? usage.technicians : usage.devices;
  return Math.round(unit * Math.max(qty, 1) * 100) / 100;
}

export type Resource = 'technicians' | 'devices' | 'aiCredits';

/** Ressources dont la consommation actuelle dépasse les quotas du plan visé (blocage d'une rétrogradation). */
export function exceededResources(planId: PlanId, sub: Pick<Subscription, 'technicians' | 'devices' | 'aiCredits'>, specs: Record<PlanId, PlanSpec> = PLANS): Resource[] {
  const plan = specs[planId];
  const out: Resource[] = [];
  if (plan.technicians !== null && sub.technicians.used > plan.technicians) out.push('technicians');
  if (plan.devices !== null && sub.devices.used > plan.devices) out.push('devices');
  if (plan.aiCredits !== null && sub.aiCredits.used > plan.aiCredits) out.push('aiCredits');
  return out;
}

/** Jours restants (arrondi au supérieur) avant une date ; 0 si dépassée. */
export function daysLeft(iso: string | undefined, now = Date.now()): number {
  if (!iso) return 0;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - now) / 86_400_000));
}
